// AIDEY'S OWN COMPOSER FOR ONE CONTACT (Carla, live, 2026-10-21).
//
// Email opened a compose panel BELOW Tags and Log contact, out of view, so the button looked dead;
// Book call built a mailto: link, which does nothing in the app or the iPhone app. Both now open THIS,
// in place of the pop-up's body (where she is looking), with "← Back" to the summary. It takes the
// focus as it opens: Subject when it is empty, else Message (Book call fills both, with her link).
//
// It sends through POST /api/email/send and then records the email on the contact's history; if that
// second step fails it says so (the email went, the history did not).
import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';
import { showToast } from '../../utils/toast';
import { Button, Field, Input, Textarea } from '../../ui';
import SendingFrom from '../Email/SendingFrom';
import './Contacts.css';

export default function ContactComposer({ contact, seed = null, title = 'New email', onBack, onSent }) {
  const [subject, setSubject] = useState(seed?.subject || '');
  const [body, setBody] = useState(seed?.body || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const rootRef = useRef(null);
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);

  // Where she is looking: the composer's top in view, and the first field to fill focused.
  useEffect(() => {
    rootRef.current?.scrollIntoView?.({ block: 'start' });
    const target = seed?.subject ? bodyRef.current : subjectRef.current;
    if (target) {
      target.focus({ preventScroll: true });
      if (seed?.body && target === bodyRef.current) {
        try { const n = target.value.length; target.setSelectionRange(n, n); } catch { /* not a text field */ }
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function send() {
    setBusy(true); setError('');
    try {
      await api('/api/email/send', { method: 'POST', body: JSON.stringify({ to: contact.email, subject, body }) });
    } catch (e) { setError(messageFromError(e, 'Could not send.')); setBusy(false); return; }
    try {
      await api(`/api/contacts/${contact.id}/interactions`, {
        method: 'POST',
        body: JSON.stringify({ type: 'email', direction: 'outbound', summary: subject || 'Email sent' }),
      });
      showToast('Email sent', { type: 'success' });
    } catch {
      showToast('Email sent, but it could not be added to this contact\'s history.', { type: 'info' });
    }
    setBusy(false);
    onSent?.();
  }

  return (
    <div ref={rootRef} className="contacts-composer" data-testid="contact-composer" role="group" aria-label={`${title} to ${contact.name}`}>
      <button type="button" className="contacts-back" onClick={onBack}><span aria-hidden="true">← </span>Back</button>
      <h3 className="contacts-composer__title">{title}</h3>
      <p className="contacts-help">To {contact.name} · {contact.email}</p>
      <SendingFrom to={contact.email} />
      <Field label="Subject"><Input ref={subjectRef} value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
      <Field label="Message"><Textarea ref={bodyRef} rows={8} value={body} onChange={(e) => setBody(e.target.value)} /></Field>
      {error && <p className="contacts-error" role="alert">{error}</p>}
      <div className="contacts-composer__actions">
        <Button variant="confirm" size="sm" disabled={busy || !subject.trim() || !body.trim()} onClick={send}>
          {busy ? 'Sending…' : 'Send'}
        </Button>
        <Button variant="tertiary" size="sm" onClick={onBack} disabled={busy}>Cancel</Button>
      </div>
    </div>
  );
}
